import React, {useEffect, useState} from 'react'
import axios from 'axios'
import Swal from 'sweetalert2'
import {
  createCustomCourseSchedule,
  getCustomCourseSchedule,
  deleteCustomCourseSchedule,
} from '../CustomController'

const API_URL = process.env.REACT_APP_API_URL
const GET_SEMESTERS_URL = `${API_URL}/Custom/GetSemesters`
const GET_MY_DEPARTMENT_SEMESTER_COURSES_URL = `${API_URL}/Custom/GetMyDepartmentSemesterCourses`
const GET_TIMESLOTS_URL = `${API_URL}/Custom/GetCourseTimeSlots`

const dayNames = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar']

interface TimeSlot {
  id: number
  day: number
  startTime?: string
  endTime?: string
  isAvailable?: boolean
  conflicts?: string[]
}

const DepartmentCourseScheduler: React.FC = () => {
  const [semesters, setSemesters] = useState<any[]>([])
  const [selectedSemester, setSelectedSemester] = useState<number | null>(null)
  const [courses, setCourses] = useState<any[]>([])
  const [selectedCourse, setSelectedCourse] = useState<any | null>(null)
  const [timeSlots, setTimeSlots] = useState<TimeSlot[]>([])
  const [selectedTheory, setSelectedTheory] = useState<number[]>([])
  const [selectedApp1, setSelectedApp1] = useState<number[]>([])
  const [selectedApp2, setSelectedApp2] = useState<number[]>([])
  const [selectedApp3, setSelectedApp3] = useState<number[]>([])
  const [selectedLab, setSelectedLab] = useState<number[]>([])
  const [activeDay, setActiveDay] = useState<number | null>(null)
  const [plannedTheory, setPlannedTheory] = useState<number[]>([])
  const [plannedApp1, setPlannedApp1] = useState<number[]>([])
  const [plannedApp2, setPlannedApp2] = useState<number[]>([])
  const [plannedApp3, setPlannedApp3] = useState<number[]>([])
  const [plannedLab, setPlannedLab] = useState<number[]>([])
  const [slotNotes, setSlotNotes] = useState<Record<number, string>>({})
  const [plannedNotes, setPlannedNotes] = useState<Record<number, string>>({})

  useEffect(() => {
    axios
      .get(GET_SEMESTERS_URL)
      .then((res) => setSemesters(res.data || []))
      .catch(console.error)
  }, [])

  const loadCourses = (semesterId: number) => {
    axios
      .get(`${GET_MY_DEPARTMENT_SEMESTER_COURSES_URL}?semesterId=${semesterId}`)
      .then((res) => setCourses(res.data || []))
      .catch(console.error)
  }

  useEffect(() => {
    if (!selectedSemester) return
    loadCourses(selectedSemester)
    setSelectedCourse(null)
    setSelectedTheory([])
    setSelectedApp1([])
    setSelectedApp2([])
    setSelectedApp3([])
    setSelectedLab([])
    setTimeSlots([])
    setActiveDay(null)
    setPlannedTheory([])
    setPlannedApp1([])
    setPlannedApp2([])
    setPlannedApp3([])
    setPlannedLab([])
    setSlotNotes({})
    setPlannedNotes({})
  }, [selectedSemester])

  useEffect(() => {
    if (!selectedSemester || !selectedCourse) return
    axios
      .get(
        `${GET_TIMESLOTS_URL}?semesterId=${selectedSemester}&semesterCourseId=${selectedCourse.id}`
      )
      .then((res) => {
        const data: TimeSlot[] = res.data || []
        setTimeSlots(data)
        const days = Array.from(new Set(data.map((ts) => ts.day))).sort((a, b) => a - b)
        setActiveDay(days[0] ?? null)
      })
      .catch(console.error)
    getCustomCourseSchedule(selectedCourse.id)
      .then((res) => {
        const theory = res?.theory || []
        const app1 = res?.application1 || []
        const app2 = res?.application2 || []
        const app3 = res?.application3 || []
        const lab = res?.laboratory || []
        const noteMap: Record<number, string> = {}
        ;[...theory, ...app1, ...app2, ...app3, ...lab].forEach((x: any) => {
          noteMap[x.timeSlotId] = x.detail || ''
        })
        setPlannedTheory(theory.map((x: any) => x.timeSlotId))
        setPlannedApp1(app1.map((x: any) => x.timeSlotId))
        setPlannedApp2(app2.map((x: any) => x.timeSlotId))
        setPlannedApp3(app3.map((x: any) => x.timeSlotId))
        setPlannedLab(lab.map((x: any) => x.timeSlotId))
        setSelectedTheory(theory.map((x: any) => x.timeSlotId))
        setSelectedApp1(app1.map((x: any) => x.timeSlotId))
        setSelectedApp2(app2.map((x: any) => x.timeSlotId))
        setSelectedApp3(app3.map((x: any) => x.timeSlotId))
        setSelectedLab(lab.map((x: any) => x.timeSlotId))
        setSlotNotes(noteMap)
        setPlannedNotes(noteMap)
      })
      .catch(console.error)
  }, [selectedCourse, selectedSemester])

  const toggle = (
    id: number,
    type: 'theory' | 'app1' | 'app2' | 'app3' | 'lab'
  ) => {
    const next = {
      theory: [...selectedTheory],
      app1: [...selectedApp1],
      app2: [...selectedApp2],
      app3: [...selectedApp3],
      lab: [...selectedLab],
    }

    if (next[type].includes(id)) {
      next[type] = next[type].filter((x) => x !== id)
    } else {
      Object.keys(next).forEach((key) => {
        if (key !== type) {
          next[key as keyof typeof next] = next[key as keyof typeof next].filter(
            (x) => x !== id
          )
        }
      })
      next[type].push(id)
    }

    setSelectedTheory(next.theory)
    setSelectedApp1(next.app1)
    setSelectedApp2(next.app2)
    setSelectedApp3(next.app3)
    setSelectedLab(next.lab)

    if (
      !next.theory.includes(id) &&
      !next.app1.includes(id) &&
      !next.app2.includes(id) &&
      !next.app3.includes(id) &&
      !next.lab.includes(id)
    ) {
      const {[id]: _, ...rest} = slotNotes
      setSlotNotes(rest)
    }
  }

  const save = async () => {
    if (!selectedCourse) return
    const newTheory = selectedTheory.filter(
      (id) => !plannedTheory.includes(id) || slotNotes[id] !== plannedNotes[id]
    )
    const newApp1 = selectedApp1.filter(
      (id) => !plannedApp1.includes(id) || slotNotes[id] !== plannedNotes[id]
    )
    const newApp2 = selectedApp2.filter(
      (id) => !plannedApp2.includes(id) || slotNotes[id] !== plannedNotes[id]
    )
    const newApp3 = selectedApp3.filter(
      (id) => !plannedApp3.includes(id) || slotNotes[id] !== plannedNotes[id]
    )
    const newLab = selectedLab.filter(
      (id) => !plannedLab.includes(id) || slotNotes[id] !== plannedNotes[id]
    )

    const removedTheory = plannedTheory.filter(
      (id) => !selectedTheory.includes(id) || slotNotes[id] !== plannedNotes[id]
    )
    const removedApp1 = plannedApp1.filter(
      (id) => !selectedApp1.includes(id) || slotNotes[id] !== plannedNotes[id]
    )
    const removedApp2 = plannedApp2.filter(
      (id) => !selectedApp2.includes(id) || slotNotes[id] !== plannedNotes[id]
    )
    const removedApp3 = plannedApp3.filter(
      (id) => !selectedApp3.includes(id) || slotNotes[id] !== plannedNotes[id]
    )
    const removedLab = plannedLab.filter(
      (id) => !selectedLab.includes(id) || slotNotes[id] !== plannedNotes[id]
    )

    const conflictSelected = timeSlots.filter(
      (ts) =>
        (selectedTheory.includes(ts.id) ||
          selectedApp1.includes(ts.id) ||
          selectedApp2.includes(ts.id) ||
          selectedApp3.includes(ts.id) ||
          selectedLab.includes(ts.id)) &&
        ts.conflicts &&
        ts.conflicts.length > 0
    )
    if (conflictSelected.length > 0) {
      const result = await Swal.fire({
        title: 'Çakışma var',
        text: 'Seçilen zaman dilimlerinde çakışmalar var. Devam edilsin mi?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Evet',
        cancelButtonText: 'Hayır',
      })
      if (!result.isConfirmed) return
    }
    if (selectedApp1.length || selectedApp2.length || selectedApp3.length) {
      const confirm = await Swal.fire({
        title: 'Uygulama Tipi Onayı',
        html: 'UYGULAMA I<br/>Dersin sorumlu ve/veya görevli öğretim elemanlarının uygulama alanında görevli olmayıp, birden fazla kurum ya da birimde o kurum ya da birimin görevlilerinin mentorluğunda yapılan uygulama dersleridir.<br/><br/>UYGULAMA II<br/>Dersin sorumlu ve/veya görevli öğretim elemanlarının uygulama alanlarında çalışma yetkisine sahip olduğu uygulama dersleridir. Dersin sorumlu ve/veya görevli öğretim elemanları ders süresinin tamamında öğrenci ile birlikte uygulama alanlarında bulunurlar.<br/><br/>UYGULAMA III<br/>Dersin sorumlu ve/veya görevli öğretim elemanlarının uygulama alanlarında çalışma yetkisine sahip olduğu, öğretim elemanın sorumluluğunda öğrencinin hastaya girişimsel işlem yaptığı uygulama dersleridir.<br/>Seçimleriniz bu tanımlara uygun mu?',
        icon: 'question',
        width: 800,
        showCancelButton: true,
        confirmButtonText: 'Evet',
        cancelButtonText: 'Hayır',
      })
      if (!confirm.isConfirmed) return
    }
    let warn = false
    if (selectedTheory.length > (selectedCourse.weeklyTheoryHours || 0)) warn = true
    if (
      selectedApp1.length + selectedApp2.length + selectedApp3.length >
      (selectedCourse.weeklyPracticeHours || 0)
    )
      warn = true
    if (selectedLab.length > (selectedCourse.weeklyLaboratoryHours || 0)) warn = true
    if (warn) {
      const result = await Swal.fire({
        title: 'Emin misiniz?',
        text: 'Seçili saatler dersin haftalık saatini aşıyor. Devam edilsin mi?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Evet',
        cancelButtonText: 'Hayır',
      })
      if (!result.isConfirmed) return
    }

    const codePart = selectedCourse.code || selectedCourse.courseCode
    const courseLabel = codePart ? `${selectedCourse.name} (${codePart})` : selectedCourse.name
    const promises: Promise<any>[] = []
    newTheory.forEach((tsId) => {
      promises.push(
        createCustomCourseSchedule({
          semesterCourseId: selectedCourse.id,
          timeSlotId: tsId,
          name: `${courseLabel} - Teorik`,
          detail: slotNotes[tsId],
        })
      )
    })
    newApp1.forEach((tsId) => {
      promises.push(
        createCustomCourseSchedule({
          semesterCourseId: selectedCourse.id,
          timeSlotId: tsId,
          name: `${courseLabel} - Uygulama 1`,
          detail: slotNotes[tsId],
        })
      )
    })
    newApp2.forEach((tsId) => {
      promises.push(
        createCustomCourseSchedule({
          semesterCourseId: selectedCourse.id,
          timeSlotId: tsId,
          name: `${courseLabel} - Uygulama 2`,
          detail: slotNotes[tsId],
        })
      )
    })
    newApp3.forEach((tsId) => {
      promises.push(
        createCustomCourseSchedule({
          semesterCourseId: selectedCourse.id,
          timeSlotId: tsId,
          name: `${courseLabel} - Uygulama 3`,
          detail: slotNotes[tsId],
        })
      )
    })
    newLab.forEach((tsId) => {
      promises.push(
        createCustomCourseSchedule({
          semesterCourseId: selectedCourse.id,
          timeSlotId: tsId,
          name: `${courseLabel} - Laboratuvar`,
          detail: slotNotes[tsId],
        })
      )
    })
    removedTheory.forEach((tsId) => {
      promises.push(deleteCustomCourseSchedule(selectedCourse.id, tsId))
    })
    removedApp1.forEach((tsId) => {
      promises.push(deleteCustomCourseSchedule(selectedCourse.id, tsId))
    })
    removedApp2.forEach((tsId) => {
      promises.push(deleteCustomCourseSchedule(selectedCourse.id, tsId))
    })
    removedApp3.forEach((tsId) => {
      promises.push(deleteCustomCourseSchedule(selectedCourse.id, tsId))
    })
    removedLab.forEach((tsId) => {
      promises.push(deleteCustomCourseSchedule(selectedCourse.id, tsId))
    })
    try {
      await Promise.all(promises)
      await Swal.fire('Kaydedildi', '', 'success')
      if (selectedSemester) {
        loadCourses(selectedSemester)
      }
      setSelectedCourse(null)
      setSelectedTheory([])
      setSelectedApp1([])
      setSelectedApp2([])
      setSelectedApp3([])
      setSelectedLab([])
      setTimeSlots([])
      setPlannedTheory([])
      setPlannedApp1([])
      setPlannedApp2([])
      setPlannedApp3([])
      setPlannedLab([])
      setSlotNotes({})
      setPlannedNotes({})
      setActiveDay(null)
    } catch (err: any) {
      Swal.fire('Hata', err.response?.data || err.message, 'error')
    }
  }

  const groupedTimeSlots = timeSlots.reduce((acc: Record<number, TimeSlot[]>, ts: TimeSlot) => {
    const day = ts.day
    if (!acc[day]) acc[day] = []
    acc[day].push(ts)
    return acc
  }, {})

  return (
    <div className='container mt-4'>
      <h2 className='mb-4'>Bölüm Ders Planlayıcı</h2>
      <div className='mb-3'>
        <label>Dönem</label>
        <select
          className='form-control'
          value={selectedSemester || ''}
          onChange={(e) => setSelectedSemester(Number(e.target.value))}
        >
          <option value='' disabled>
            Seçiniz
          </option>
          {semesters.map((s: any) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
      {courses.length > 0 && (
        <div className='mb-3'>
          <label>Ders</label>
          <select
            className='form-control'
            value={selectedCourse?.id || ''}
            onChange={(e) => {
              setSelectedCourse(courses.find((c) => c.id === Number(e.target.value)) || null)
              setActiveDay(null)
            }}
          >
            <option value='' disabled>
              Seçiniz
            </option>
            {courses.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name} - {c.instructorName}
              </option>
            ))}
          </select>
        </div>
      )}
      {selectedCourse && (
        <>
          {(() => {
            const dayKeys = Object.keys(groupedTimeSlots)
              .map(Number)
              .sort((a, b) => a - b)
            return (
              <>
                <div className='d-flex flex-wrap gap-2 mb-3'>
                  {(selectedCourse.weeklyTheoryHours || 0) > 0 && (
                    <div className='p-2 rounded bg-primary text-white'>
                      Teorik: {selectedCourse.weeklyTheoryHours || 0} | Planlanan:{' '}
                      {selectedTheory.length}
                    </div>
                  )}
                  {(selectedCourse.weeklyPracticeHours || 0) > 0 && (
                    <div className='p-2 rounded bg-success text-white'>
                      Uygulama: {selectedCourse.weeklyPracticeHours || 0} | Planlanan:{' '}
                      {selectedApp1.length + selectedApp2.length + selectedApp3.length}
                    </div>
                  )}
                  {(selectedCourse.weeklyLaboratoryHours || 0) > 0 && (
                    <div className='p-2 rounded bg-danger text-white'>
                      Laboratuvar: {selectedCourse.weeklyLaboratoryHours || 0} | Planlanan:{' '}
                      {selectedLab.length}
                    </div>
                  )}
                </div>
                <ul className='nav nav-tabs mb-3'>
                  {dayKeys.map((day) => (
                    <li className='nav-item' key={day}>
                      <button
                        className={`nav-link ${activeDay === day ? 'active' : ''}`}
                        onClick={() => setActiveDay(day)}
                      >
                        {dayNames[day - 1] || `Gün ${day}`}
                      </button>
                    </li>
                  ))}
                </ul>
                {activeDay && groupedTimeSlots[activeDay] && (
                  <div className='row'>
                    {groupedTimeSlots[activeDay].map((ts: any) => (
                      <div key={ts.id} className='col-md-3 mb-3'>
                        <div className='card p-2'>
                          <div className='fw-bold'>
                            {ts.startTime?.substring(0, 5)} - {ts.endTime?.substring(0, 5)}
                          </div>
                          {(selectedCourse.weeklyTheoryHours || 0) > 0 && (
                            <div className='form-check'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={selectedTheory.includes(ts.id)}
                                onChange={() => toggle(ts.id, 'theory')}
                                id={`t${ts.id}`}
                                disabled={!ts.isAvailable && !selectedTheory.includes(ts.id)}
                              />
                              <label className='form-check-label' htmlFor={`t${ts.id}`}>
                                Teorik
                              </label>
                            </div>
                          )}
                          {(selectedCourse.weeklyPracticeHours || 0) > 0 && (
                            <div className='form-check'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={selectedApp1.includes(ts.id)}
                                onChange={() => toggle(ts.id, 'app1')}
                                id={`a1${ts.id}`}
                              />
                              <label className='form-check-label' htmlFor={`a1${ts.id}`}>
                                Uygulama 1
                              </label>
                            </div>
                          )}
                          {(selectedCourse.weeklyPracticeHours || 0) > 0 && (
                            <div className='form-check'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={selectedApp2.includes(ts.id)}
                                onChange={() => toggle(ts.id, 'app2')}
                                id={`a2${ts.id}`}
                              />
                              <label className='form-check-label' htmlFor={`a2${ts.id}`}>
                                Uygulama 2
                              </label>
                            </div>
                          )}
                          {(selectedCourse.weeklyPracticeHours || 0) > 0 && (
                            <div className='form-check'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={selectedApp3.includes(ts.id)}
                                onChange={() => toggle(ts.id, 'app3')}
                                id={`a3${ts.id}`}
                              />
                              <label className='form-check-label' htmlFor={`a3${ts.id}`}>
                                Uygulama 3
                              </label>
                            </div>
                          )}
                          {(selectedCourse.weeklyLaboratoryHours || 0) > 0 && (
                            <div className='form-check'>
                              <input
                                className='form-check-input'
                                type='checkbox'
                                checked={selectedLab.includes(ts.id)}
                                onChange={() => toggle(ts.id, 'lab')}
                                id={`l${ts.id}`}
                              />
                              <label className='form-check-label' htmlFor={`l${ts.id}`}>
                                Laboratuvar
                              </label>
                            </div>
                          )}
                          {(selectedTheory.includes(ts.id) ||
                            selectedApp1.includes(ts.id) ||
                            selectedApp2.includes(ts.id) ||
                            selectedApp3.includes(ts.id) ||
                            selectedLab.includes(ts.id) ||
                            slotNotes[ts.id] !== undefined) && (
                            <input
                              type='text'
                              className='form-control mt-2'
                              placeholder='Açıklama'
                              value={slotNotes[ts.id] || ''}
                              onChange={(e) =>
                                setSlotNotes({...slotNotes, [ts.id]: e.target.value})
                              }
                            />
                          )}
                          {ts.conflicts?.length > 0 && (
                            <div className='text-danger small mt-1'>
                              Dolu: {ts.conflicts.join(', ')}
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )
          })()}
          <div className='mb-3'>
            {selectedTheory.length > (selectedCourse.weeklyTheoryHours || 0) && (
              <div className='text-warning'>
                Teorik saat sayısı ({selectedTheory.length}) haftalık ders saatini aşıyor (
                {selectedCourse.weeklyTheoryHours}).
              </div>
            )}
            {selectedApp1.length + selectedApp2.length + selectedApp3.length >
              (selectedCourse.weeklyPracticeHours || 0) && (
              <div className='text-warning'>
                Uygulama saat sayısı (
                {selectedApp1.length + selectedApp2.length + selectedApp3.length}) haftalık
                uygulama saatini aşıyor ({selectedCourse.weeklyPracticeHours}).
              </div>
            )}
            {selectedLab.length > (selectedCourse.weeklyLaboratoryHours || 0) && (
              <div className='text-warning'>
                Laboratuvar saat sayısı ({selectedLab.length}) haftalık laboratuvar saatini aşıyor (
                {selectedCourse.weeklyLaboratoryHours}).
              </div>
            )}
          </div>
          <button className='btn btn-primary' onClick={save}>
            Kaydet
          </button>
        </>
      )}
    </div>
  )
}

export default DepartmentCourseScheduler
